import React, { useState, useEffect, useContext } from 'react';
import { DataContext } from '../../init/getData';
import { Form, Row, Col, Card, Button, Alert, Table, Tag, Spin, Tabs, Select, Checkbox, Tooltip, Input, Progress } from 'antd'; // Added Progress import
import { 
  PlayCircleOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  EyeOutlined,
  BankOutlined,
  ShoppingOutlined,
  WalletOutlined,
  ContainerOutlined,
  HomeOutlined,
  LineChartOutlined,
  SettingOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import toast from 'react-hot-toast';

const { Option } = Select;

function YearClosing() {
  const { userData, selectedCompany, api, warehouseData } = useContext(DataContext);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [canClose, setCanClose] = useState(false);
  const [accountBalances, setAccountBalances] = useState(null);
  const [accountStatistics, setAccountStatistics] = useState([]);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [selectedAccountTypes, setSelectedAccountTypes] = useState(['1', '2', '3', '4', '5']);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [includeStockClosing, setIncludeStockClosing] = useState(false);

  const [newYearForm] = Form.useForm();

  // Account type configuration
  const accountTypeOptions = [
    { 
      value: '1', 
      label: 'Capital Accounts', 
      description: 'Equity, retained earnings, capital contributions',
      icon: <HomeOutlined />,
      color: '#52c41a'
    },
    { 
      value: '2', 
      label: 'Asset Accounts', 
      description: 'Fixed assets, investments, prepayments',
      icon: <BankOutlined />,
      color: '#1890ff'
    },
    { 
      value: '3', 
      label: 'Inventory Accounts', 
      description: 'Stock, raw materials, work in progress',
      icon: <ContainerOutlined />,
      color: '#fa8c16'
    },
    { 
      value: '4', 
      label: 'Client/Supplier Accounts', 
      description: 'Receivables, payables, customer/supplier balances',
      icon: <ShoppingOutlined />,
      color: '#722ed1'
    },
    { 
      value: '5', 
      label: 'Cash/Bank Accounts', 
      description: 'Cash on hand, bank balances, checks',
      icon: <WalletOutlined />,
      color: '#13c2c2'
    }
  ];

  useEffect(() => {
    loadYears();
  }, [selectedCompany]);

  const loadYears = async () => {
    if (!selectedCompany?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/year/getYears?companyId=${selectedCompany.id}`);
      if (response.data.status) {
        setYears(response.data.data);
      }
    } catch (error) {
      console.error('Error loading years:', error);
      toast.error('Failed to load years');
    } finally {
      setLoading(false);
    }
  };

  const loadAccountBalances = async (yearId) => {
    try {
      setLoadingAccounts(true);
      const response = await api.get(`/year/getAccountBalancesByCategory?companyId=${selectedCompany.id}&yearId=${yearId}`);
      if (response.data.status) {
        setAccountBalances(response.data.data);
        setShowPreview(true);
      } else {
        toast.error(response.data.message || 'Failed to load account balances');
      }
    } catch (error) {
      console.error('Error loading account balances:', error);
      toast.error('Failed to load account balances');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadStatistics = async (yearId) => {
    try {
      const response = await api.get(`/year/getAccountTypeStatistics?companyId=${selectedCompany.id}&yearId=${yearId}`);
      if (response.data.status) {
        setAccountStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const loadYearClosingPreview = async (yearId) => {
    try {
      const response = await api.get(`/year/getYearClosingPreview?companyId=${selectedCompany.id}&yearId=${yearId}`);
      if (response.data.status) {
        const { suggestedNewYear } = response.data.data;
        newYearForm.setFieldsValue({
          newYear: suggestedNewYear.year,
          startDate: suggestedNewYear.startDate,
          endDate: suggestedNewYear.endDate
        });
      }
    } catch (error) {
      console.error('Error loading year closing preview:', error);
    }
  };

  const checkCanClose = async (yearId) => {
    try {
      const response = await api.get(`/year/canCloseYear?yearId=${yearId}`);
      if (response.data.status) {
        setCanClose(response.data.canClose);
      }
    } catch (error) {
      console.error('Error checking year:', error);
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    checkCanClose(year.id);
    loadStatistics(year.id);
    loadYearClosingPreview(year.id);
    // Don't load account balances automatically - user will click button
  };

  const handleLoadAccounts = () => {
    if (!selectedYear) return;
    loadAccountBalances(selectedYear.id);
  };

  const handleAccountTypeChange = (selectedValues) => {
    setSelectedAccountTypes(selectedValues);
  };

  const selectAllAccountTypes = () => {
    setSelectedAccountTypes(['1', '2', '3', '4', '5']);
  };

  const clearAllAccountTypes = () => {
    setSelectedAccountTypes([]);
  };

  const closeYear = async () => {
    if (!selectedYear) return;

    // Validate at least one account type is selected
    if (selectedAccountTypes.length === 0) {
      toast.error('Please select at least one account type to transfer');
      return;
    }

    const values = await newYearForm.validateFields();
    
    setClosing(true);
    try {
      console.log('Closing year with data:', {
        yearId: selectedYear.id,
        newYearData: values,
        accountTypesToTransfer: selectedAccountTypes,
        includeStockClosing: includeStockClosing // Add this
      });
      
      // MODIFY THIS PART - Check if stock closing is included
      if (includeStockClosing) {
        // Call unified function for both year and stock closing
        const response = await api.post('/year/closeYearAndStock', {
          yearId: selectedYear.id,
          newYearData: {
            year: values.newYear,
            startDate: values.startDate,
            endDate: values.endDate,
            companyId: selectedCompany.id
          },
          userId: userData.id,
          accountTypesToTransfer: selectedAccountTypes,
          warehouseIds: [] // You can make this configurable if needed
        });

        console.log('Year and stock closing response:', response.data);

        if (response.data.status) {
          toast.success('Year and stock closed successfully!');
          
          // Reset form and state
          newYearForm.resetFields();
          setSelectedYear(null);
          setShowPreview(false);
          setShowStatistics(false);
          setAccountBalances(null);
          setSelectedAccountTypes(['1', '2', '3', '4', '5']); // Reset to default
          setIncludeStockClosing(false); // Reset checkbox
          loadYears(); // Reload years list
        } else {
          toast.error(response.data.message);
        }
      } else {
        // Call regular closeYear (only accounts)
        const response = await api.post('/year/closeYear', {
          yearId: selectedYear.id,
          newYearData: {
            year: values.newYear,
            startDate: values.startDate,
            endDate: values.endDate,
            companyId: selectedCompany.id
          },
          userId: userData.id,
          accountTypesToTransfer: selectedAccountTypes
        });

        console.log('Year closing response:', response.data);

        if (response.data.status) {
          toast.success('Year closed successfully!');
          
          // Reset form and state
          newYearForm.resetFields();
          setSelectedYear(null);
          setShowPreview(false);
          setShowStatistics(false);
          setAccountBalances(null);
          setSelectedAccountTypes(['1', '2', '3', '4', '5']); // Reset to default
          setIncludeStockClosing(false); // Reset checkbox
          loadYears(); // Reload years list
        } else {
          toast.error(response.data.message);
        }
      }
    } catch (error) {
      console.error('Error closing year:', error);
      toast.error(error.response?.data?.message || 'Failed to close year');
    } finally {
      setClosing(false);
    }
  };

  const getCategoryIcon = (category) => {
    const option = accountTypeOptions.find(o => o.value === category);
    return option ? React.cloneElement(option.icon, { style: { color: option.color } }) : <LineChartOutlined style={{ color: '#f5222d' }} />;
  };

  const getCategoryName = (category) => {
    const option = accountTypeOptions.find(o => o.value === category);
    return option ? option.label : 'Other';
  };

  const getAccountCategoryName = (category) => {
    const typeId = categoryToTypeMap[category];
    if (typeId) {
      const option = accountTypeOptions.find(o => o.value === typeId);
      return option ? option.label : 'Other';
    }
    return 'Other';
  };

  const columns = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate'
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate'
    },
    {
      title: 'Status',
      dataIndex: 'isClosed',
      key: 'status',
      render: (isClosed) => (
        <Tag color={isClosed ? 'red' : 'green'}>
          {isClosed ? 'Closed' : 'Open'}
        </Tag>
      )
    },
    {
      title: 'Transactions',
      dataIndex: ['_count', 'journalVouchers'],
      key: 'transactions',
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          disabled={record.isClosed}
          onClick={() => handleYearSelect(record)}
          icon={<PlayCircleOutlined />}
        >
          Close Year
        </Button>
      )
    }
  ];

  const categoryColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        const option = accountTypeOptions.find(o => o.value === category);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {option ? React.cloneElement(option.icon, { style: { color: option.color } }) : <LineChartOutlined />}
            <span>{option ? option.label : 'Other'}</span>
          </div>
        );
      }
    },
    {
      title: 'Account Count',
      dataIndex: 'totalAccounts',
      key: 'totalAccounts',
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: 'Accounts with Balance',
      dataIndex: 'accountsWithBalance',
      key: 'accountsWithBalance',
      render: (count) => <Tag color="cyan">{count}</Tag>
    },
    {
      title: 'Total Balance',
      dataIndex: 'totalBalance',
      key: 'totalBalance',
      render: (balance) => (
        <Tag color={balance >= 0 ? 'green' : 'red'}>
          {balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
        </Tag>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={selectedAccountTypes.includes(record.category) ? 'success' : 'default'}>
          {selectedAccountTypes.includes(record.category) ? 'Will Transfer' : "Won't Transfer"}
        </Tag>
      )
    }
  ];

  /// Add this mapping function
  const categoryToTypeMap = {
    'capital': '1',
    'asset': '2',
    'inventory': '3',
    'clientSupplier': '4',
    'cashBank': '5',
    'other': '0'
  };

  const typeToCategoryMap = {
    '1': 'capital',
    '2': 'asset',
    '3': 'inventory',
    '4': 'clientSupplier',
    '5': 'cashBank',
    '0': 'other'
  };

  // Update the filter function
  const filteredAccounts = (accountBalances?.allAccounts || [])
    .filter(account => {
      if (!searchText) return true;
      const searchLower = searchText.toLowerCase();
      return (
        (account.accountId && account.accountId.toLowerCase().includes(searchLower)) ||
        (account.name && account.name.toLowerCase().includes(searchLower)) ||
        (account.parentEnglish && account.parentEnglish.toLowerCase().includes(searchLower))
      );
    })
    .filter(account => {
      // Map the category to type ID for comparison
      const accountTypeId = categoryToTypeMap[account.category] || '0';
      return selectedAccountTypes.includes(accountTypeId);
    });

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Year Management" loading={loading}>
            <Table
              columns={columns}
              dataSource={years}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </Col>

        {selectedYear && (
          <Col span={24}>
            <Card 
              title={`Close Year: ${selectedYear.year}`}
              extra={
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="text"
                    icon={<SettingOutlined />}
                    onClick={() => setShowStatistics(!showStatistics)}
                  >
                    {showStatistics ? 'Hide Statistics' : 'Show Statistics'}
                  </Button>
                  <Button
                    type="text"
                    icon={<CloseCircleOutlined />}
                    onClick={() => {
                      setSelectedYear(null);
                      setShowPreview(false);
                      setShowStatistics(false);
                      setAccountBalances(null);
                      newYearForm.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              }
            >
              {!canClose && (
                <Alert
                  message="Cannot Close Year"
                  description="Please ensure all sessions are closed before proceeding with year closing."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {showStatistics && (
                <Card 
                  title="Account Type Statistics" 
                  size="small" 
                  style={{ marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="small" onClick={selectAllAccountTypes}>Select All</Button>
                      <Button size="small" onClick={clearAllAccountTypes}>Clear All</Button>
                    </div>
                  }
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select account types to transfer..."
                        value={selectedAccountTypes}
                        onChange={handleAccountTypeChange}
                        optionLabelProp="label"
                        dropdownRender={menu => (
                          <div>
                            {menu}
                            <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0' }}>
                              <Checkbox
                                checked={selectedAccountTypes.length === accountTypeOptions.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    selectAllAccountTypes();
                                  } else {
                                    clearAllAccountTypes();
                                  }
                                }}
                              >
                                Select All Account Types
                              </Checkbox>
                            </div>
                          </div>
                        )}
                      >
                        {accountTypeOptions.map(option => (
                          <Option key={option.value} value={option.value} label={option.label}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ color: option.color }}>{option.icon}</div>
                              <div>
                                <div style={{ fontWeight: 'bold' }}>{option.label}</div>
                                <div style={{ fontSize: 12, color: '#666' }}>{option.description}</div>
                              </div>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={12}>
                      <Alert
                        message="Account Type Selection"
                        description={`${selectedAccountTypes.length} of ${accountTypeOptions.length} account types selected for transfer`}
                        type="info"
                        showIcon
                      />
                    </Col>
                  </Row>

                  <Table
                    columns={categoryColumns}
                    dataSource={accountStatistics}
                    rowKey="category"
                    size="small"
                    pagination={false}
                    style={{ marginTop: 16 }}
                  />

                  <div style={{ marginTop: 16, padding: 16, background: '#f6ffed', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span><strong>Selected Types:</strong></span>
                      <span>
                        {selectedAccountTypes.map(type => {
                          const option = accountTypeOptions.find(o => o.value === type);
                          return (
                            <Tag key={type} color="blue" style={{ marginLeft: 4 }}>
                              {option?.label}
                            </Tag>
                          );
                        })}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span><strong>Total Accounts to Transfer:</strong></span>
                      <span>
                        <strong>
                          {accountStatistics
                            .filter(stat => selectedAccountTypes.includes(stat.category))
                            .reduce((sum, stat) => sum + (stat.accountsWithBalance || 0), 0)}
                        </strong>
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span><strong>Total Balance to Transfer:</strong></span>
                      <span>
                        <strong>
                          {accountStatistics
                            .filter(stat => selectedAccountTypes.includes(stat.category))
                            .reduce((sum, stat) => sum + Math.abs(stat.totalBalance || 0), 0)
                            .toLocaleString(undefined, { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })}
                        </strong>
                      </span>
                    </div>
                  </div>
                </Card>
              )}

              <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <Tabs.TabPane tab="Year Closing" key="summary">
                  <Row gutter={16}>
                    <Col span={12}>
                     <Card title="New Year Configuration" size="small">
                        <Form
                          form={newYearForm}
                          layout="vertical"
                          onFinish={closeYear}
                        >
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item
                                label="New Year"
                                name="newYear"
                                rules={[{ required: true, message: 'Please enter new year' }]}
                              >
                                <input 
                                  type="number" 
                                  className="ant-input"
                                  placeholder="e.g., 2024"
                                  min={parseInt(selectedYear.year) + 1}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Start Date"
                                name="startDate"
                                rules={[{ required: true, message: 'Please select start date' }]}
                              >
                                <input 
                                  type="date" 
                                  className="ant-input"
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="End Date"
                                name="endDate"
                                rules={[{ required: true, message: 'Please select end date' }]}
                              >
                                <input 
                                  type="date" 
                                  className="ant-input"
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          {/* ADD THE CHECKBOX HERE */}
                          <Form.Item>
                            <Checkbox
                              checked={includeStockClosing}
                              onChange={(e) => setIncludeStockClosing(e.target.checked)}
                              style={{ marginBottom: 16 }}
                            >
                              <strong>Also transfer stock to new year</strong>
                              <div style={{ fontSize: 12, color: '#666', marginLeft: 24 }}>
                                Creates OpeningStock journal voucher with all product quantities
                              </div>
                            </Checkbox>
                          </Form.Item>

                          <Alert
                            message="Account Transfer Information"
                            description={`The following account types will be transferred: ${selectedAccountTypes.map(t => {
                              const option = accountTypeOptions.find(o => o.value === t);
                              return option ? option.label : t;
                            }).join(', ')}${includeStockClosing ? ' (including stock)' : ''}`}
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                          />

                          <Form.Item>
                            <Button
                              type="primary"
                              htmlType="submit"
                              loading={closing}
                              disabled={!canClose || selectedAccountTypes.length === 0}
                              icon={<CheckCircleOutlined />}
                              size="large"
                            >
                              {includeStockClosing ? 'Close Year & Stock' : 'Close Year'} for {selectedYear.year} and Open New Year
                            </Button>
                            {selectedAccountTypes.length === 0 && (
                              <div style={{ marginTop: 8, color: '#f5222d' }}>
                                Please select at least one account type to transfer
                              </div>
                            )}
                          </Form.Item>
                        </Form>
                      </Card>
                    </Col>

                    <Col span={12}>
                      <Card 
                        title="Transfer Preview" 
                        size="small"
                        extra={
                          <Tooltip title="View detailed account information">
                            <Button 
                              type="text" 
                              icon={<EyeOutlined />} 
                              onClick={() => setActiveTab('details')}
                              size="small"
                            />
                          </Tooltip>
                        }
                      >
                        {accountStatistics.length > 0 ? (
                          accountStatistics.map(stat => (
                            <div 
                              key={stat.category} 
                              style={{ 
                                marginBottom: 12, 
                                padding: 12, 
                                border: '1px solid #f0f0f0',
                                borderRadius: 6,
                                opacity: selectedAccountTypes.includes(stat.category) ? 1 : 0.6
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {getCategoryIcon(stat.category)}
                                  <div>
                                    <div style={{ fontWeight: 'bold' }}>{getCategoryName(stat.category)}</div>
                                    <div style={{ fontSize: 12, color: '#666' }}>
                                      {stat.accountsWithBalance || 0} accounts with balance
                                    </div>
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                                    {stat.totalBalance?.toLocaleString(undefined, { 
                                      minimumFractionDigits: 2, 
                                      maximumFractionDigits: 2 
                                    }) || '0.00'}
                                  </div>
                                  <Tag color={selectedAccountTypes.includes(stat.category) ? 'success' : 'default'} style={{ marginTop: 4 }}>
                                    {selectedAccountTypes.includes(stat.category) ? 'Will Transfer' : "Won't Transfer"}
                                  </Tag>
                                </div>
                              </div>
                              {stat.totalBalance !== 0 && (
                                <Progress 
                                  percent={Math.min(Math.abs(stat.totalBalance) / 10000 * 100, 100)} 
                                  size="small" 
                                  strokeColor={selectedAccountTypes.includes(stat.category) ? 
                                    accountTypeOptions.find(o => o.value === stat.category)?.color : 
                                    '#d9d9d9'
                                  }
                                  style={{ marginTop: 8 }}
                                />
                              )}
                            </div>
                          ))
                        ) : (
                          <div style={{ textAlign: 'center', padding: 20 }}>
                            <p>No account statistics available</p>
                            <Button 
                              type="primary" 
                              icon={<ReloadOutlined />}
                              onClick={handleLoadAccounts}
                              loading={loadingAccounts}
                            >
                              Load Account Balances
                            </Button>
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>
                </Tabs.TabPane>

                <Tabs.TabPane tab="Account Details" key="details">
                  <Card size="small">
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <strong>Filter by Account Type:</strong>
                          <Select
                            mode="multiple"
                            style={{ width: 400 }}
                            placeholder="Filter accounts by type..."
                            value={selectedAccountTypes}
                            onChange={handleAccountTypeChange}
                            options={accountTypeOptions}
                          />
                        </div>
                      </Col>
                      <Col span={8}>
                        <Input
                          placeholder="Search accounts..."
                          prefix={<SearchOutlined />}
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          allowClear
                        />
                      </Col>
                      <Col span={4}>
                        <Button
                          type="primary"
                          icon={<ReloadOutlined />}
                          onClick={handleLoadAccounts}
                          loading={loadingAccounts}
                          style={{ width: '100%' }}
                        >
                          {accountBalances ? 'Refresh' : 'Load'} Accounts
                        </Button>
                      </Col>
                    </Row>
                    
                    {loadingAccounts ? (
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                        <p>Loading account balances...</p>
                      </div>
                    ) : accountBalances?.allAccounts?.length > 0 ? (
                      <>
                        <Alert
                          message={`Showing ${filteredAccounts.length} of ${accountBalances.allAccounts.length} accounts`}
                          type="info"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                        <Table
                          columns={[
                            { 
                              title: 'Account ID', 
                              dataIndex: 'accountId', 
                              key: 'accountId',
                              render: (id, record) => (
                                <div>
                                  <strong>{id}</strong>
                                  {!selectedAccountTypes.includes(record.category) && (
                                    <div style={{ fontSize: 11, color: '#f5222d' }}>Not transferring</div>
                                  )}
                                </div>
                              )
                            },
                            { 
                              title: 'Account Name', 
                              dataIndex: 'name', 
                              key: 'name',
                              render: (name, record) => (
                                <div>
                                  {name || 'Unnamed Account'}
                                  <div style={{ fontSize: 12, color: '#666' }}>
                                    {getAccountCategoryName(record.category)}
                                  </div>
                                </div>
                              )
                            },
                            { 
                              title: 'Parent Account', 
                              dataIndex: 'parentEnglish', 
                              key: 'parent',
                              render: (parent) => parent || '-'
                            },
                            { 
                              title: 'Balance', 
                              dataIndex: 'balance', 
                              key: 'balance',
                              render: (balance) => (
                                <Tag color={balance >= 0 ? 'blue' : 'red'}>
                                  {balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                </Tag>
                              )
                            },
                            { 
                              title: 'Type', 
                              dataIndex: 'isDebit', 
                              key: 'type',
                              render: (isDebit) => (
                                <Tag color={isDebit ? 'green' : 'orange'}>
                                  {isDebit ? 'Debit' : 'Credit'}
                                </Tag>
                              )
                            },
                            { 
                          title: 'Status', 
                          key: 'transferStatus',
                          render: (_, record) => {
                            const accountTypeId = categoryToTypeMap[record.category] || '0';
                            return (
                              <Tag color={selectedAccountTypes.includes(accountTypeId) ? 'success' : 'default'}>
                                {selectedAccountTypes.includes(accountTypeId) ? 'Will Transfer' : "Won't Transfer"}
                              </Tag>
                            ); 
                          }
                            }
                          ]}
                          dataSource={filteredAccounts}
                          rowKey="id"
                          size="small"
                          pagination={{ pageSize: 10 }}
                          scroll={{ y: 300 }}
                        />
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <Alert
                          message="No Account Data Loaded"
                          description="Click the 'Load Accounts' button to load account balances for this year."
                          type="info"
                          showIcon
                        />
                        <Button 
                          type="primary" 
                          icon={<ReloadOutlined />}
                          onClick={handleLoadAccounts}
                          loading={loadingAccounts}
                          style={{ marginTop: 20 }}
                        >
                          Load Account Balances
                        </Button>
                      </div>
                    )}
                  </Card>
                </Tabs.TabPane>
              </Tabs>

              <Card type="inner" title="Year Closing Information" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ padding: 12 }}>
                      <h4>What Gets Transferred?</h4>
                      <ul>
                        {accountTypeOptions.map(option => (
                          <li key={option.value} style={{ 
                            opacity: selectedAccountTypes.includes(option.value) ? 1 : 0.6,
                            marginBottom: 8 
                          }}>
                            <strong style={{ color: option.color }}>{option.label}:</strong> {option.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ padding: 12 }}>
                      <h4>What Happens?</h4>
                      <ul>
                        <li>Selected account balances are carried forward</li>
                        <li>Unselected accounts reset to zero balance</li>
                        <li>Opening balance JV is created automatically</li>
                        <li>Current year is marked as closed</li>
                        <li>No new transactions can be added to closed year</li>
                      </ul>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ padding: 12 }}>
                      <h4>Important Notes</h4>
                      <ul>
                        <li>Review account types before closing</li>
                        <li>Backup your data before proceeding</li>
                        <li>Ensure all sessions are closed</li>
                        <li>Verify balances match your expectations</li>
                        <li>Year closing cannot be undone</li>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}

export default YearClosing;